import {
  renderPublishingDocumentPages,
  type PublishingDocument,
} from '@tov-plus/publishing';
import {
  SegmentedControl,
  SegmentedControlItem,
  TovButton,
  TovIcon,
  Input,
  Textarea,
  buildOpeningExperiencePreviewData,
  InvitationSurface,
  msg,
  ProfileAvatar,
  TovShimmer,
  useToast,
  getTovFormSubmitState,
  useTovAsyncDebouncedCallback,
  useTovForm,
  useTovFormStore,
} from '@tov-plus/ui';
import {
  invalidateQueryGroups,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';
import {
  startTransition,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { guestApi } from '../../app/api';
import { StatePanel } from '../../components/StatePanel';
import { useGuestAccess } from '../../contexts/GuestAccessContext';
import { guestQueryKeys } from '../../lib/data/guestQueryKeys';
import {
  getParticipantEventDraftKey,
  getRsvpDraftStorageKey,
  getRsvpErrorMessage,
  isDeadlinePassed,
  type EventDraft,
  type GuestRsvpPageState,
  type SubmitRsvpStatus,
} from './rsvpDraftState';
import {
  buildRsvpSubmissionEvents,
  buildInitialDrafts,
  getDraftSyncSignature,
  getPersistedDrafts,
  persistDraftsToStorage,
} from './rsvpPageDrafts';

export function PublishedInvitationSurface(props: {
  document: PublishingDocument;
  imageUrlByAssetId: Record<string, string | null>;
  fallback: ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const firstPage = props.document.pages[0];
  const [renderState, setRenderState] = useState<
    'rendering' | 'ready' | 'error'
  >('rendering');

  useEffect(() => {
    let cancelled = false;
    const container = containerRef.current;

    if (!container) {
      return;
    }

    setRenderState('rendering');
    container.replaceChildren();

    void renderPublishingDocumentPages({
      document: props.document,
      imageUrlByAssetId: props.imageUrlByAssetId,
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
    })
      .then((canvases) => {
        if (cancelled) {
          return;
        }

        container.replaceChildren();
        for (const canvas of canvases) {
          const canvasElement = canvas as HTMLCanvasElement;
          canvasElement.classList.add('guest-rsvp-published-invitation__page');
          canvasElement.style.width = 'auto';
          canvasElement.style.height = 'auto';
          canvasElement.style.maxWidth = '100%';
          canvasElement.style.maxHeight =
            'var(--guest-rsvp-invitation-max-height)';
          container.append(canvasElement);
        }
        setRenderState(canvases.length > 0 ? 'ready' : 'error');
      })
      .catch(() => {
        if (!cancelled) {
          setRenderState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [props.document, props.imageUrlByAssetId]);

  if (renderState === 'error') {
    return props.fallback;
  }

  return (
    <div
      className="guest-rsvp-published-invitation"
      data-testid="guest-rsvp-published-invitation"
    >
      {renderState === 'rendering' ? (
        <TovShimmer
          className="guest-rsvp-published-invitation__shimmer"
          style={
            firstPage
              ? ({
                  '--guest-rsvp-invitation-aspect-ratio': `${firstPage.width} / ${firstPage.height}`,
                } as CSSProperties)
              : undefined
          }
        />
      ) : null}
      <div
        className="guest-rsvp-published-invitation__pages"
        ref={containerRef}
      />
    </div>
  );
}

export function RsvpPage() {
  const access = useGuestAccess();
  const queryClient = useTovQueryClient();
  const notifications = useToast();
  const [pageState, setPageState] = useState<GuestRsvpPageState | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [, setErrorMessage] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, EventDraft>>({});
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [draftSyncState, setDraftSyncState] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const lastSyncedDraftSignatureRef = useRef<string | null>(null);
  const syncRsvpDraft = useTovAsyncDebouncedCallback(
    async (input: {
      occasionSlug: string;
      guestToken: string;
      persistedDrafts: Record<string, EventDraft>;
      draftSyncSignature: string;
    }) => {
      setDraftSyncState('saving');
      try {
        await guestApi.updateDraftState(input.occasionSlug, input.guestToken, {
          rsvpDraft:
            Object.keys(input.persistedDrafts).length > 0
              ? input.persistedDrafts
              : null,
        });
        lastSyncedDraftSignatureRef.current = input.draftSyncSignature;
        await invalidateQueryGroups(queryClient, [
          guestQueryKeys.drafts(input.occasionSlug, input.guestToken),
          guestQueryKeys.rsvp(input.occasionSlug, input.guestToken),
        ]);
        setDraftSyncState('saved');
      } catch {
        setDraftSyncState('idle');
        notifications.error(msg('pages.rsvp.rsvp_page.rsvp_draft_save_error'));
      }
    },
    { wait: 700 },
  );
  const [addedPerson, setAddedPerson] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });
  const [addPersonExpanded, setAddPersonExpanded] = useState(false);
  const [householdNote, setHouseholdNote] = useState('');
  const [addPersonState, setAddPersonState] = useState<'idle' | 'submitting'>(
    'idle',
  );
  const readyOccasionSlug =
    access.status === 'ready' ? access.experience.occasion.slug : null;
  const readyGuestToken = access.status === 'ready' ? access.guestToken : null;
  const rsvpPageQuery = useTovQuery({
    queryKey:
      readyOccasionSlug && readyGuestToken
        ? guestQueryKeys.rsvp(readyOccasionSlug, readyGuestToken, 'page')
        : guestQueryKeys.rsvp('pending', 'pending', 'page'),
    queryFn: async () => {
      const [response, remoteDraftState] = await Promise.all([
        guestApi.getRsvp(readyOccasionSlug!, readyGuestToken!),
        guestApi.getDraftState(readyOccasionSlug!, readyGuestToken!),
      ]);
      return { response, remoteDraftState };
    },
    enabled: Boolean(readyOccasionSlug && readyGuestToken),
  });
  const rsvpForm = useTovForm({
    defaultValues: {},
    onSubmit: async () => {
      if (access.status !== 'ready' || !pageState) {
        return;
      }

      const editableEvents = pageState.events.filter(
        (event) => !isDeadlinePassed(event.deadlineAt),
      );

      if (editableEvents.length === 0) {
        const message = msg(
          'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_all_rsvp_response_windows_for_this_invite_are',
        );
        notifications.error(message);
        throw new Error(message);
      }

      try {
        const submissionEvents = buildRsvpSubmissionEvents({
          events: pageState.events,
          identityContext: pageState.identityContext,
          drafts,
          householdNote,
        });

        await guestApi.submitRsvp(access.occasionSlug, access.guestToken, {
          events: submissionEvents,
        });
        notifications.success(
          msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_saved_response'),
        );
        setDraftSyncState('saved');
        window.localStorage.removeItem(
          getRsvpDraftStorageKey(access.occasionSlug, access.guestToken),
        );
        try {
          await guestApi.updateDraftState(
            access.occasionSlug,
            access.guestToken,
            {
              rsvpDraft: null,
            },
          );
          lastSyncedDraftSignatureRef.current = getDraftSyncSignature({});
        } catch {
          setDraftSyncState('idle');
          notifications.error(
            msg('pages.rsvp.rsvp_page.rsvp_draft_save_error'),
          );
        }
        await invalidateRsvpQueries();
        await rsvpPageQuery.refetch();
        access.refresh();
      } catch (error) {
        notifications.error(getRsvpErrorMessage(error));
        throw error;
      }
    },
  });
  const submitState = useTovFormStore(rsvpForm.store, (state) =>
    getTovFormSubmitState({
      isSubmitSuccessful: state.isSubmitSuccessful,
      isSubmitting: state.isSubmitting,
      submissionAttempts: state.submissionAttempts,
    }),
  );
  useEffect(() => {
    if (!rsvpPageQuery.data || !readyOccasionSlug || !readyGuestToken) {
      return;
    }
    const { response, remoteDraftState } = rsvpPageQuery.data;
    try {
      const initialDrafts = buildInitialDrafts({
        events: response.events,
        identityContext: response.identityContext,
        occasionSlug: readyOccasionSlug,
        guestToken: readyGuestToken,
        remoteDraft: remoteDraftState.rsvpDraft,
        storedDrafts: window.localStorage.getItem(
          getRsvpDraftStorageKey(readyOccasionSlug, readyGuestToken),
        ),
      });
      const initialPersistedDrafts = getPersistedDrafts({
        events: response.events,
        identityContext: response.identityContext,
        drafts: initialDrafts,
      });
      lastSyncedDraftSignatureRef.current = getDraftSyncSignature(
        initialPersistedDrafts,
      );
      startTransition(() => {
        setPageState(response);
        setDrafts(initialDrafts);
        setDraftHydrated(true);
        setDraftSyncState('idle');
        setStatus('ready');
      });
    } catch (error) {
      startTransition(() => {
        setStatus('error');
        setErrorMessage(getRsvpErrorMessage(error));
      });
    }
  }, [readyGuestToken, readyOccasionSlug, rsvpPageQuery.data]);
  async function invalidateRsvpQueries() {
    if (access.status !== 'ready') {
      return;
    }
    await invalidateQueryGroups(queryClient, [
      guestQueryKeys.rsvp(access.occasionSlug, access.guestToken),
      guestQueryKeys.forms(access.occasionSlug, access.guestToken),
      guestQueryKeys.bootstrap(access.occasionSlug, access.guestToken),
    ]);
  }
  useEffect(() => {
    if (rsvpPageQuery.isLoading) {
      startTransition(() => {
        setStatus('loading');
        setErrorMessage(null);
      });
    }
    if (rsvpPageQuery.isError) {
      startTransition(() => {
        setStatus('error');
        setErrorMessage(getRsvpErrorMessage(rsvpPageQuery.error));
      });
    }
  }, [rsvpPageQuery.error, rsvpPageQuery.isError, rsvpPageQuery.isLoading]);
  useEffect(() => {
    if (access.status !== 'ready' || pageState === null) {
      return;
    }
    persistDraftsToStorage({
      occasionSlug: access.occasionSlug,
      guestToken: access.guestToken,
      drafts: getPersistedDrafts({
        events: pageState.events,
        identityContext: pageState.identityContext,
        drafts,
      }),
    });
  }, [
    access.status,
    access.occasionSlug,
    access.guestToken,
    drafts,
    pageState,
  ]);
  useEffect(() => {
    if (access.status !== 'ready' || pageState === null || !draftHydrated) {
      return;
    }
    const persistedDrafts = getPersistedDrafts({
      events: pageState.events,
      identityContext: pageState.identityContext,
      drafts,
    });
    const draftSyncSignature = getDraftSyncSignature(persistedDrafts);
    if (draftSyncSignature === lastSyncedDraftSignatureRef.current) {
      return;
    }
    void syncRsvpDraft({
      occasionSlug: access.occasionSlug,
      guestToken: access.guestToken,
      persistedDrafts,
      draftSyncSignature,
    });
  }, [
    access.status,
    access.occasionSlug,
    access.guestToken,
    draftHydrated,
    drafts,
    pageState,
    syncRsvpDraft,
  ]);
  if (access.status !== 'ready') {
    return null;
  }
  if (status === 'loading') {
    return <StatePanel preset="loading" />;
  }
  if (status === 'error' || !pageState) {
    return (
      <StatePanel
        preset="loading"
        actions={
          <TovButton onClick={() => void rsvpPageQuery.refetch()}>
            {msg('general.actions.retry')}
          </TovButton>
        }
      />
    );
  }
  const editableEvents = pageState.events.filter(
    (event) => !isDeadlinePassed(event.deadlineAt),
  );
  const hasEditableEvents = editableEvents.length > 0;
  const primaryEvent = pageState.events[0] ?? null;
  const locale = access.experience.localizationContext.resolvedLocaleCode;
  const invitationData = buildOpeningExperiencePreviewData({
    title: pageState.unitDisplayName || access.experience.occasion.displayName,
    dateLabel: pageState.events[0]?.startsAt
      ? new Date(pageState.events[0].startsAt).toLocaleDateString(locale)
      : access.experience.occasion.displayName,
    locationLabel:
      pageState.events[0]?.locationName ??
      access.experience.occasion.displayName,
    themeLabel:
      access.experience.designTheme?.themeName ??
      access.experience.occasion.displayName,
  });
  const getEventParticipantResponses = (
    eventState: GuestRsvpPageState['events'][number],
  ) =>
    eventState.participantResponses.length > 0
      ? eventState.participantResponses
      : pageState.identityContext.participantActorIds.map(
          (participantActorId, index) => ({
            participantActorId,
            participantDisplayName:
              pageState.identityContext.participantDisplayNames[index] ??
              participantActorId,
            response:
              eventState.response?.participantActorIds.includes(
                participantActorId,
              ) === true
                ? eventState.response
                : null,
          }),
        );
  const participantRows =
    primaryEvent !== null
      ? getEventParticipantResponses(primaryEvent)
      : pageState.identityContext.participantActorIds.map(
          (participantActorId, index) => ({
            participantActorId,
            participantDisplayName:
              pageState.identityContext.participantDisplayNames[index] ??
              participantActorId,
            response: null,
          }),
        );
  const currentGuestActorId = pageState.identityContext.accessActorId;
  const getParticipantPrimaryStatus = (participantActorId: string) => {
    if (!primaryEvent) {
      return 'pending';
    }

    const draftKey = getParticipantEventDraftKey(
      primaryEvent.eventId,
      participantActorId,
    );

    return drafts[draftKey]?.status ?? 'pending';
  };
  const setParticipantStatus = (
    participantActorId: string,
    statusValue: SubmitRsvpStatus,
  ) => {
    setDrafts((current) => {
      const nextDrafts = { ...current };

      for (const eventState of editableEvents) {
        const isParticipantInvited = getEventParticipantResponses(
          eventState,
        ).some(
          (participant) =>
            participant.participantActorId === participantActorId,
        );

        if (!isParticipantInvited) {
          continue;
        }

        const draftKey = getParticipantEventDraftKey(
          eventState.eventId,
          participantActorId,
        );
        nextDrafts[draftKey] = {
          ...current[draftKey],
          status: statusValue,
        };
      }

      return nextDrafts;
    });
  };
  const setAllParticipantStatuses = (statusValue: SubmitRsvpStatus) => {
    setDrafts((current) => {
      const nextDrafts = { ...current };

      for (const eventState of editableEvents) {
        for (const participant of getEventParticipantResponses(eventState)) {
          const draftKey = getParticipantEventDraftKey(
            eventState.eventId,
            participant.participantActorId,
          );
          nextDrafts[draftKey] = {
            ...current[draftKey],
            status: statusValue,
          };
        }
      }

      return nextDrafts;
    });
  };
  const participantPrimaryStatuses = participantRows.map((participant) =>
    getParticipantPrimaryStatus(participant.participantActorId),
  );
  const allParticipantsStatus =
    participantPrimaryStatuses.length > 0 &&
    participantPrimaryStatuses.every(
      (statusValue) => statusValue === participantPrimaryStatuses[0],
    )
      ? participantPrimaryStatuses[0]
      : '';
  const eventResponseSummaries = pageState.events.map((eventState) => {
    const eventParticipants = getEventParticipantResponses(eventState);
    const counts = eventParticipants.reduce(
      (currentCounts, participant) => {
        const draftKey = getParticipantEventDraftKey(
          eventState.eventId,
          participant.participantActorId,
        );
        const statusValue = drafts[draftKey]?.status ?? 'pending';

        return {
          ...currentCounts,
          [statusValue]: currentCounts[statusValue] + 1,
        };
      },
      {
        attending: 0,
        declined: 0,
        pending: 0,
      } satisfies Record<SubmitRsvpStatus, number>,
    );

    return {
      eventState,
      counts,
      isClosed: isDeadlinePassed(eventState.deadlineAt),
      participantCount: eventParticipants.length,
    };
  });
  const totalEditableResponses = editableEvents.reduce(
    (totalCount, eventState) =>
      totalCount + getEventParticipantResponses(eventState).length,
    0,
  );
  const completedEditableResponses = editableEvents.reduce(
    (totalCount, eventState) =>
      totalCount +
      getEventParticipantResponses(eventState).filter((participant) => {
        const draftKey = getParticipantEventDraftKey(
          eventState.eventId,
          participant.participantActorId,
        );

        return drafts[draftKey]?.status !== 'pending';
      }).length,
    0,
  );
  const pendingEditableResponses = Math.max(
    totalEditableResponses - completedEditableResponses,
    0,
  );
  const rsvpProgressItems: Array<{
    icon: 'users' | 'calendar-blank' | 'check' | 'chat-centered-text';
    label: string;
    value: string;
  }> = [
    {
      icon: 'users',
      label: msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_guests'),
      value: String(participantRows.length),
    },
    {
      icon: 'calendar-blank',
      label: msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_events'),
      value: `${editableEvents.length}/${pageState.events.length}`,
    },
    {
      icon: 'check',
      label: msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_pending'),
      value: String(pendingEditableResponses),
    },
    {
      icon: 'chat-centered-text',
      label: msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_host_note'),
      value:
        householdNote.trim().length > 0
          ? msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_added')
          : msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_optional'),
    },
  ];
  const rsvpNextStep = !hasEditableEvents
    ? msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_rsvp_closed')
    : pendingEditableResponses > 0
      ? msg(
          'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_choose_each_guest_response',
        )
      : msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_ready_to_save_response');
  const formatRsvpDate = (value: string | null) =>
    value
      ? new Date(value).toLocaleDateString(locale, {
          month: 'short',
          day: 'numeric',
        })
      : msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_date_pending');
  async function submitAddedPerson() {
    if (access.status !== 'ready') {
      return;
    }
    if (
      addedPerson.firstName.trim().length === 0 ||
      addedPerson.lastName.trim().length === 0
    ) {
      notifications.error(
        msg(
          'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_add_guest_name_required',
        ),
      );
      return;
    }
    setAddPersonState('submitting');
    try {
      await guestApi.addPersonFromGuestRsvp(
        access.occasionSlug,
        access.guestToken,
        {
          firstName: addedPerson.firstName,
          lastName: addedPerson.lastName,
          email: addedPerson.email || null,
          phone: addedPerson.phone || null,
        },
      );
      setAddedPerson({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      });
      setAddPersonExpanded(false);
      notifications.success(
        msg(
          'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_guest_added_to_this_invitation',
        ),
      );
      await invalidateRsvpQueries();
      await rsvpPageQuery.refetch();
      access.refresh();
    } catch (error) {
      notifications.error(getRsvpErrorMessage(error));
    } finally {
      setAddPersonState('idle');
    }
  }
  const isPublicLinkInvitation =
    pageState.invitation.audienceRuleType === 'anyone_with_link';
  const draftSyncLabel =
    draftSyncState === 'saving'
      ? msg('pages.rsvp.rsvp_page.rsvp_draft_saving')
      : draftSyncState === 'saved'
        ? msg('pages.rsvp.rsvp_page.rsvp_draft_saved')
        : draftSyncState === 'error'
          ? msg('pages.rsvp.rsvp_page.rsvp_draft_save_error')
          : msg('pages.rsvp.rsvp_page.rsvp_draft_autosave_ready');
  return (
    <div className="guest-rsvp-page">
      <div className="guest-rsvp-main">
        <section className="guest-rsvp-main-card">
          <section className="guest-rsvp-invitation-surface">
            {pageState.publishableInvitation ? (
              <PublishedInvitationSurface
                document={pageState.publishableInvitation.document}
                imageUrlByAssetId={
                  pageState.publishableInvitation.imageUrlByAssetId
                }
                fallback={<InvitationSurface invitationData={invitationData} />}
              />
            ) : (
              <InvitationSurface invitationData={invitationData} />
            )}
          </section>

          <form
            data-testid="guest-rsvp-form"
            onSubmit={async (event) => {
              event.preventDefault();
              try {
                await rsvpForm.handleSubmit();
              } catch {
                // Toast feedback is emitted by the form submit handler.
              }
            }}
            className="guest-rsvp-form"
          >
            <section className="guest-rsvp-progress-panel">
              <div className="guest-rsvp-progress-panel__next">
                <span className="guest-rsvp-progress-panel__icon">
                  <TovIcon name={hasEditableEvents ? 'check' : 'lock'} />
                </span>
                <div>
                  <small>
                    {msg(
                      'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_whats_next',
                    )}
                  </small>
                  <strong>{rsvpNextStep}</strong>
                </div>
              </div>
              <div className="guest-rsvp-progress-strip">
                {rsvpProgressItems.map((item) => (
                  <span className="guest-rsvp-progress-item" key={item.label}>
                    <TovIcon name={item.icon} />
                    <span>
                      <small>{item.label}</small>
                      <strong>{item.value}</strong>
                    </span>
                  </span>
                ))}
              </div>
            </section>

            {eventResponseSummaries.length > 0 ? (
              <section className="guest-rsvp-event-summary">
                <div className="guest-rsvp-event-summary__heading">
                  <strong>
                    {msg(
                      'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_event_response_snapshot',
                    )}
                  </strong>
                  <small>
                    {msg(
                      'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_review_before_saving',
                    )}
                  </small>
                </div>
                <div className="guest-rsvp-event-summary__grid">
                  {eventResponseSummaries.map(
                    ({ counts, eventState, isClosed, participantCount }) => (
                      <article
                        className="guest-rsvp-event-summary-card"
                        key={eventState.eventId}
                      >
                        <div className="guest-rsvp-event-summary-card__title">
                          <span>
                            <TovIcon name="calendar-blank" />
                          </span>
                          <div>
                            <strong>{eventState.displayName}</strong>
                            <small>
                              {formatRsvpDate(eventState.startsAt)}
                              {eventState.locationName
                                ? ` - ${eventState.locationName}`
                                : ''}
                            </small>
                          </div>
                        </div>
                        <div className="guest-rsvp-event-summary-card__counts">
                          <span>
                            <TovIcon name="check" />
                            {counts.attending}
                          </span>
                          <span>
                            <TovIcon name="question" />
                            {counts.pending}
                          </span>
                          <span>
                            <TovIcon name="x" />
                            {counts.declined}
                          </span>
                        </div>
                        <small className="guest-rsvp-event-summary-card__meta">
                          {isClosed
                            ? msg(
                                'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_response_window_closed',
                              )
                            : `${participantCount} ${msg(
                                'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_people',
                              )}`}
                        </small>
                      </article>
                    ),
                  )}
                </div>
              </section>
            ) : null}

            {!isPublicLinkInvitation ? (
              <section className="guest-rsvp-household-panel">
                <div className="guest-rsvp-household-panel__heading">
                  <span className="guest-rsvp-household-panel__icon">
                    <TovIcon name="users" />
                  </span>
                  <div>
                    <h2>
                      {msg(
                        'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_responding_for',
                      )}
                    </h2>
                    <p>
                      {participantRows.length}{' '}
                      {msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_people')}{' '}
                      {msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_invited')}
                    </p>
                  </div>
                </div>
                <div
                  className="guest-rsvp-status-legend"
                  aria-label={msg('pages.rsvp.rsvp_page.rsvp_status_legend')}
                >
                  <span>
                    <TovIcon name="check" />
                    {msg('pages.rsvp.rsvp_page.rsvp_status_attending')}
                  </span>
                  <span>
                    <TovIcon name="question" />
                    {msg('pages.rsvp.rsvp_page.rsvp_status_pending')}
                  </span>
                  <span>
                    <TovIcon name="x" />
                    {msg('pages.rsvp.rsvp_page.rsvp_status_declined')}
                  </span>
                </div>

                <div className="guest-rsvp-participant-list">
                  <div className="guest-rsvp-participant guest-rsvp-participant--all">
                    <span className="guest-rsvp-participant__avatar guest-rsvp-participant__avatar--all">
                      <TovIcon name="users" />
                    </span>
                    <span className="guest-rsvp-participant__identity">
                      <strong>{msg('general.filters.all')}</strong>
                    </span>
                    <RsvpStatusControl
                      ariaLabel={msg('general.filters.all')}
                      disabled={!hasEditableEvents}
                      value={allParticipantsStatus}
                      onValueChange={setAllParticipantStatuses}
                    />
                  </div>
                  {participantRows.map((participant) => {
                    const primaryStatus = getParticipantPrimaryStatus(
                      participant.participantActorId,
                    );
                    const isCurrentGuest =
                      currentGuestActorId !== null &&
                      participant.participantActorId === currentGuestActorId;

                    return (
                      <div
                        className="guest-rsvp-participant"
                        key={participant.participantActorId}
                      >
                        <ProfileAvatar
                          actorId={participant.participantActorId}
                          alt={participant.participantDisplayName}
                          className="guest-rsvp-participant__avatar"
                          displayName={participant.participantDisplayName}
                          facehashProps={{ interactive: false }}
                          fallbackSeed="tov-plus-contact"
                        />
                        <span className="guest-rsvp-participant__identity">
                          <strong>{participant.participantDisplayName}</strong>
                          {isCurrentGuest ? (
                            <small>
                              {msg(
                                'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_you',
                              )}
                            </small>
                          ) : null}
                        </span>
                        <RsvpStatusControl
                          ariaLabel={participant.participantDisplayName}
                          disabled={!hasEditableEvents}
                          value={primaryStatus}
                          onValueChange={(nextValue) =>
                            setParticipantStatus(
                              participant.participantActorId,
                              nextValue,
                            )
                          }
                        />
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {pageState.invitation.allowAddOthers ? (
              <section className="guest-rsvp-add-person-panel">
                <TovButton
                  aria-expanded={addPersonExpanded}
                  className="guest-rsvp-add-person-panel__trigger"
                  type="button"
                  variant="ghost"
                  onClick={() =>
                    setAddPersonExpanded((currentValue) => !currentValue)
                  }
                >
                  <span className="guest-rsvp-add-person-panel__icon">
                    <TovIcon name="plus" />
                  </span>
                  <span>
                    <strong>
                      {msg('pages.public_invite_join.add_another_guest')}
                    </strong>
                    <small>
                      {msg(
                        'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_add_someone_not_listed_above',
                      )}
                    </small>
                  </span>
                  <TovIcon name="caret-right" />
                </TovButton>

                {addPersonExpanded ? (
                  <div className="guest-rsvp-add-person-panel__fields">
                    <label className="field-stack">
                      <span>{msg('general.fields.first_name.label')}</span>
                      <Input
                        onChange={(event) =>
                          setAddedPerson((current) => ({
                            ...current,
                            firstName: event.target.value,
                          }))
                        }
                        required
                        value={addedPerson.firstName}
                      />
                    </label>
                    <label className="field-stack">
                      <span>{msg('general.fields.last_name.label')}</span>
                      <Input
                        onChange={(event) =>
                          setAddedPerson((current) => ({
                            ...current,
                            lastName: event.target.value,
                          }))
                        }
                        required
                        value={addedPerson.lastName}
                      />
                    </label>
                    <label className="field-stack">
                      <span>{msg('general.fields.email.label')}</span>
                      <Input
                        onChange={(event) =>
                          setAddedPerson((current) => ({
                            ...current,
                            email: event.target.value,
                          }))
                        }
                        type="email"
                        value={addedPerson.email}
                      />
                    </label>
                    <label className="field-stack">
                      <span>{msg('general.fields.phone.label')}</span>
                      <Input
                        onChange={(event) =>
                          setAddedPerson((current) => ({
                            ...current,
                            phone: event.target.value,
                          }))
                        }
                        value={addedPerson.phone}
                      />
                    </label>
                    <TovButton
                      disabled={
                        addPersonState === 'submitting' ||
                        addedPerson.firstName.trim().length === 0 ||
                        addedPerson.lastName.trim().length === 0
                      }
                      type="button"
                      onClick={() => void submitAddedPerson()}
                    >
                      {addPersonState === 'submitting'
                        ? msg(
                            'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_adding_guest',
                          )
                        : msg('pages.public_invite_join.add_another_guest')}
                    </TovButton>
                  </div>
                ) : null}
              </section>
            ) : null}

            <label className="guest-rsvp-host-message">
              <span>
                {msg(
                  'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_message_for_the_hosts',
                )}{' '}
                <small>
                  {msg(
                    'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_optional_parenthetical',
                  )}
                </small>
              </span>
              <Textarea
                maxLength={500}
                onChange={(event) => setHouseholdNote(event.target.value)}
                placeholder={msg(
                  'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_share_a_note_or_any_special_requests',
                )}
                rows={5}
                value={householdNote}
              />
              <small>
                {householdNote.length}
                {msg(
                  'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_character_limit_500_suffix',
                )}
              </small>
            </label>

            <div className="guest-form__actions">
              <span
                className={`guest-rsvp-draft-status guest-rsvp-draft-status--${draftSyncState}`}
                role={draftSyncState === 'error' ? 'alert' : 'status'}
              >
                <TovIcon
                  name={draftSyncState === 'error' ? 'warning-circle' : 'check'}
                />
                {draftSyncLabel}
              </span>
              <TovButton
                data-testid="guest-rsvp-submit"
                type="submit"
                disabled={submitState === 'submitting' || !hasEditableEvents}
              >
                {submitState === 'submitting'
                  ? msg('pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_saving_rsvp')
                  : hasEditableEvents
                    ? msg(
                        'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_save_rsvp_changes',
                      )
                    : msg(
                        'pages.rsvp.rsvp_page.pages_rsvp_rsvp_page_rsvp_closed',
                      )}
              </TovButton>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

function RsvpStatusControl({
  ariaLabel,
  disabled,
  onValueChange,
  value,
}: {
  ariaLabel: string;
  disabled: boolean;
  onValueChange(value: SubmitRsvpStatus): void;
  value: SubmitRsvpStatus | '';
}) {
  const options: Array<{
    icon: 'check' | 'question' | 'x';
    label: string;
    value: SubmitRsvpStatus;
  }> = [
    {
      icon: 'check',
      label: msg('pages.rsvp.rsvp_page.rsvp_status_attending'),
      value: 'attending',
    },
    {
      icon: 'question',
      label: msg('pages.rsvp.rsvp_page.rsvp_status_pending'),
      value: 'pending',
    },
    {
      icon: 'x',
      label: msg('pages.rsvp.rsvp_page.rsvp_status_declined'),
      value: 'declined',
    },
  ];

  return (
    <SegmentedControl
      aria-label={ariaLabel}
      className="guest-rsvp-status-control"
      value={value}
      onValueChange={(nextValue) => {
        if (
          nextValue === 'attending' ||
          nextValue === 'pending' ||
          nextValue === 'declined'
        ) {
          onValueChange(nextValue);
        }
      }}
    >
      {options.map((option) => (
        <SegmentedControlItem
          aria-label={option.label}
          className="guest-rsvp-status-control__item"
          disabled={disabled}
          key={option.value}
          title={option.label}
          value={option.value}
        >
          <TovIcon name={option.icon} />
        </SegmentedControlItem>
      ))}
    </SegmentedControl>
  );
}
